import { createSlice } from '@reduxjs/toolkit';
import type { JsonFailure, RequestStatus } from '@skynet/data-ws-core';
import { isOkCommand, parseResponse, registerErrorMessage } from '@skynet/data-ws-core';
import { actionOnMessage } from '@skynet/data-ws-mm';
import type {
  JsonLoanStrikesSuccess,
  JsonStrikePrice,
  StrikePrice,
  StrikePricesState,
} from './types';
import { StrikePricesCommands } from './types';
import type { SetFormStrikePricesPayload } from './actions';
import { addStrikePrice, editStrikePrice, mapFormStrikePrice, removeStrikePrice } from './actions';

registerErrorMessage(StrikePricesCommands.AddStrikePrice, {
  title: 'Add Strike Price Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

registerErrorMessage(StrikePricesCommands.EditStrikePrice, {
  title: 'Edit Strike Price Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

registerErrorMessage(StrikePricesCommands.RemoveStrikePrice, {
  title: 'Remove Strike Price Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

const allowedCommands = Object.values(StrikePricesCommands);

export const initialState: StrikePricesState = {
  error: null,
  requestStatus: null,
  strikePrices: null,
  selectedStrikePrice: null,
  formStrikePrices: null,
  resAddStrikePrice: null,
  resEditStrikePrice: null,
  resRemoveStrikePrice: null,
};

const loansStrikePricesSlice = createSlice({
  name: 'StrikePrices',
  initialState,
  reducers: {
    setFormStrikePrices(state, action: { payload: SetFormStrikePricesPayload }) {
      state.formStrikePrices = action.payload.map(mapFormStrikePrice);
    },
    setSelectedStrikePrice(state, action: { payload: StrikePrice | null }) {
      state.selectedStrikePrice = action.payload;
    },
    setStrikePricesRequestStatus(state, action: { payload: RequestStatus }) {
      state.requestStatus = action.payload;
    },
    clearErrors(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addStrikePrice, (state) => {
      state.resAddStrikePrice = 'pending';
      state.error = null;
    });
    builder.addCase(editStrikePrice, (state) => {
      state.resEditStrikePrice = 'pending';
      state.error = null;
    });
    builder.addCase(removeStrikePrice, (state) => {
      state.resRemoveStrikePrice = 'pending';
      state.error = null;
    });

    // on WS message
    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error } = parseResponse(action.payload, allowedCommands);
      if (skipProcessing || !json) return;

      console.debug(`loans/processing ${actionOnMessage.toString()}`, action, allowedCommands);

      const { command, okCommand, errorCommand } = json;

      if (isOkCommand(command) && StrikePricesCommands.AddStrikePrice === okCommand) {
        state.formStrikePrices = null;
        state.resAddStrikePrice = 'success';
        return;
      }

      if (isOkCommand(command) && okCommand === StrikePricesCommands.EditStrikePrice) {
        state.selectedStrikePrice = null;
        state.resEditStrikePrice = 'success';
        return;
      }
      if (isOkCommand(command) && okCommand === StrikePricesCommands.RemoveStrikePrice) {
        state.resRemoveStrikePrice = 'success';
        return;
      }

      if (error) {
        state.error = error;
        if (errorCommand === StrikePricesCommands.AddStrikePrice) {
          state.resAddStrikePrice = 'error';
        }
        if (errorCommand === StrikePricesCommands.EditStrikePrice) {
          state.resEditStrikePrice = 'error';
        }
        if (errorCommand === StrikePricesCommands.RemoveStrikePrice) {
          state.resRemoveStrikePrice = 'error';
        }
        return;
      }

      if (command === StrikePricesCommands.LoanStrikes) {
        const { strikes } = json as JsonLoanStrikesSuccess;
        state.strikePrices = strikes.map(
          ({
            TokensAmount,
            StrikePrice,
            StrikeDate,
            EndDate,
            StrikeType,
            ID,
            Note,
          }: JsonStrikePrice) => ({
            id: ID,
            tokensAmount: TokensAmount,
            strikePrice: StrikePrice,
            strikeDate: StrikeDate,
            endDate: EndDate,
            strikeType: StrikeType,
            note: Note,
          }),
        );
      }
    });
  },
});

export const strikePricesActions = {
  ...loansStrikePricesSlice.actions,
  addStrikePrice,
  removeStrikePrice,
  editStrikePrice,
};

export default loansStrikePricesSlice.reducer;
